<script lang="ts">
	import { type ApiModels as M } from "@entitlements/api-models"
	import { type ActiveSubscription } from "../types"
	import { openOverlay } from "overlay"
	import SubscriptionLinkList from "./SubscriptionLinkList.svelte"
	import { type Snippet } from "svelte"

	interface Props {
		title: string
		heading?: Snippet
		description: string

		unitType: string
		// Used to render lines of text when no subscription is provided
		subscriptionCopy: Array<string>
		// Called when the user clicks the "subscribe now" button
		onSubscribe: () => void

		activeSubscription?: ActiveSubscription
	}

	let {
		title,
		heading,
		description,
		subscriptionCopy,
		unitType,
		onSubscribe,
		activeSubscription,
	}: Props = $props()

	let hasMultipleSubscriptions = $derived(activeSubscription.editUrls.length > 1)

	function manageSubscriptions() {
		openOverlay(SubscriptionLinkList, {
			heading: "Manage Subscriptions",
			classHeading: "no-underline",
			props: { links: activeSubscription.editUrls },
		})
	}

	function renderDate(date: Date) {
		return date.toDateString()
	}

	function renderStatus(status: M["SubscriptionStatus"]) {
		return `${status.toLowerCase().replaceAll("_", " ")}`
	}
</script>

<div class="subscription-menu">
	{#if heading}
		{@render heading()}
	{:else}
		<h3 class="heading">{title}</h3>
	{/if}
	<div class="description">
		{description}
	</div>

	{#if activeSubscription}
		<div class="flex-row">
			<div class="details">
				<div class="value-preview">
					<div class="available">{activeSubscription.availableUnits}</div>
					<div class="total">of {activeSubscription.totalUnits}</div>
					<div class="unit">{unitType}</div>
				</div>
				{#if activeSubscription.nextRefreshTime}
					<span class="unit-description"
						>refreshes {renderDate(activeSubscription.nextRefreshTime)}</span
					>
				{/if}
			</div>
		</div>
		<div class="plan-container">
			<div class="plan">
				<span class="label">Current Plan:</span>
				<span class="plan-value">{activeSubscription.name}</span>
				<span class="status">{renderStatus(activeSubscription.status)}</span>
				{#if hasMultipleSubscriptions}
					<button class="small alt" onclick={manageSubscriptions}>Manage Subscription</button>
				{:else}
					<a href={activeSubscription.editUrls[0]} target="_blank">
						<button class="small alt">Manage Subscription</button>
					</a>
				{/if}
			</div>

			<div class="plan">
				<span class="label">Next Charge:</span>
				<span class="plan-value">{renderDate(activeSubscription.nextBillingTime)}</span>
			</div>
		</div>
	{:else}
		<div class="details">
			<div class="sub-copy">
				{#each subscriptionCopy as text}
					<span>{text}</span>
				{/each}
			</div>
			<button class="outline-button" onclick={onSubscribe}>Subscribe Now</button>
		</div>
		<div class="plan-container">
			<div class="plan">
				<span class="label">Current Plan: </span>
				<span class="plan-value">None</span>
			</div>
		</div>
	{/if}
</div>

<style>
	.subscription-menu {
		display: flex;
		flex-direction: column;
		height: 100%;
		width: 100%;
	}

	.unit-description {
		font-size: var(--text-small);
		color: var(--color-text);
	}

	.value-preview {
		display: flex;
		flex-direction: row;
		align-items: flex-end;
		gap: 0.1rem;
		font-family: var(--font-heading);
	}

	.value-preview .available {
		font-size: var(--h0);
		font-weight: 800;
		line-height: 1em;
	}

	.value-preview .total {
		font-size: var(--h2);
		color: var(--color-text);
		line-height: 1em;
		padding-bottom: 0.2rem;
		font-weight: 800;
	}

	.heading {
		font-size: var(--h1);
		margin: 0;
	}

	.sub-copy {
		margin-bottom: 1rem;
		display: flex;
		flex-direction: column;
	}

	.label {
		color: var(--color-text);
	}

	.description {
		color: var(--color-text);
		line-height: 1.4em;
	}

	.details {
		margin: 1rem 0;
		padding: 1rem;
		background-color: var(--color-bg-darker);
		border-radius: var(--br);
		width: fit-content;
		line-height: 1.4em;

		display: flex;
		flex-direction: column;
	}

	.unit {
		/* font-size: var(--h3); */
		font-family: var(--font-body);
		color: var(--color-text);
	}

	.status {
		color: var(--color-main);
	}

	.status::before {
		content: "[";
		color: var(--color-text);
	}

	.status::after {
		content: "]";
		color: var(--color-text);
	}

	.plan-container {
		margin-top: auto;
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
	}

	.plan {
		display: flex;
		flex-wrap: wrap;
		line-height: 1.2em;
		flex-direction: row;
		gap: 0.5rem;
		align-items: center;
	}

	a {
		border-bottom: none;
	}
</style>
